/**
 * Shared notification tray dropdown UI (broker / shipper / carrier).
 */
import {
  isInteractionNotificationType,
  type NotificationRole,
} from './notificationChannels';
import { isDashboardActionRequiredNotificationType } from './notificationTypes';

export const NOTIFICATION_TRAY_PAGE_SIZE = 10;

export const NOTIFICATION_TRAY_LOAD_MORE_LABEL = 'Show next 10';

/** Rolling window for bell dropdown visibility (4 weeks). */
export const NOTIFICATION_TRAY_RETENTION_DAYS = 28;

export function getNotificationTrayRetentionCutoff(now: Date = new Date()): Date {
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - NOTIFICATION_TRAY_RETENTION_DAYS);
  return cutoff;
}

export function isNotificationWithinTrayRetention(
  createdAt: Date | null | undefined,
  now: Date = new Date()
): boolean {
  if (!(createdAt instanceof Date) || Number.isNaN(createdAt.getTime())) {
    return false;
  }
  return createdAt.getTime() >= getNotificationTrayRetentionCutoff(now).getTime();
}

/**
 * Types that belong on the bell tray for this role (interaction channel only).
 * Excludes Action Required (broker/shipper dashboard) and all sidebar ops types.
 */
export function isBellTrayNotificationType(
  type: unknown,
  role: NotificationRole = 'broker'
): boolean {
  return typeof type === 'string' && type.length > 0 && isInteractionNotificationType(type, role);
}

export type BellTrayNotificationLike = {
  type?: string;
  read?: boolean;
  createdAt?: Date | null;
};

/**
 * Notifications shown in the bell dropdown (all roles).
 * Interaction channel only, within retention window.
 */
export function filterNotificationsForBellTray<T extends BellTrayNotificationLike>(
  notifications: T[],
  role: NotificationRole = 'broker',
  now: Date = new Date()
): T[] {
  return notifications.filter(
    (n) =>
      isBellTrayNotificationType(n.type, role) &&
      isNotificationWithinTrayRetention(n.createdAt, now)
  );
}

/** Unread count for the header badge — must match tray-visible unread items. */
export function countUnreadBellTrayNotifications(
  notifications: BellTrayNotificationLike[],
  role: NotificationRole = 'broker',
  now: Date = new Date()
): number {
  return filterNotificationsForBellTray(notifications, role, now).filter((n) => n.read !== true)
    .length;
}

/** @deprecated Prefer isBellTrayNotificationType — kept for Action Required exclusion checks in trays. */
export function isExcludedFromBellTrayAsActionRequired(type: unknown): boolean {
  return isDashboardActionRequiredNotificationType(type);
}
